// ── Component snapshot types (mirrored from OMS model.StorefrontSnapshot) ──────

export interface ComponentSnapshotEntry {
  type_key: string;
  type_label: string;
  component_id: string;
  name: string;
  quantity: number;
  unit_price_cents: number;
}

export interface StorefrontSnapshot {
  compound_id: string;
  display_name: string;
  sku?: string;
  unit_price_cents: number;
  selected_components?: Record<string, string>;
  selected_components_snapshot?: ComponentSnapshotEntry[];
}

// ── customer_data blob types (mirrored from the OMS model.Order blob) ─────────
// Single parser shared by every consumer so schema-drift fixes land in one place.

export interface OmsCustomerAddress {
  line1?: string;
  line2?: string;
  city?: string;
  postal_code?: string;
  country?: string;
}

export interface OmsCustomerDataBlob {
  first_name?: string;
  last_name?: string;
  name?: string;
  email?: string;
  phone?: string;
  nif?: string;
  billing_address?: OmsCustomerAddress;
  shipping_address?: OmsCustomerAddress;
}

function asAddress(value: unknown): OmsCustomerAddress | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const a = value as Record<string, unknown>;
  const str = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = a[k];
      if (v == null) continue;
      const t = (typeof v === "string" ? v : String(v)).trim();
      if (t !== "") return t;
    }
    return undefined;
  };
  return {
    line1: str("line1"),
    line2: str("line2"),
    city: str("city"),
    postal_code: str("postal_code"),
    country: str("country"),
  };
}

/**
 * Parses the customer_data JSON blob persisted on the OMS order, tolerating the
 * key variations the different write paths (checkout, builder quote, Niposom
 * fan-out) have produced over time. Returns an object with all-optional fields;
 * malformed/absent blobs yield `{}` rather than null so callers can always read
 * optional fields safely.
 */
export function parseCustomerDataBlob(raw?: string): OmsCustomerDataBlob {
  if (!raw || raw === "{}") return {};
  let data: unknown;
  try {
    data = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};

  const d = data as Record<string, unknown>;
  const billing = d.billing_address;
  const shipping = d.shipping_address;
  const str = (v: unknown): string | undefined =>
    v != null && v !== "" ? String(v) : undefined;

  const phoneRaw =
    d.phone ??
    d.phone_number ??
    d.phoneNumber ??
    (billing as Record<string, unknown> | undefined)?.phone ??
    (billing as Record<string, unknown> | undefined)?.phone_number ??
    (shipping as Record<string, unknown> | undefined)?.phone ??
    (shipping as Record<string, unknown> | undefined)?.phone_number ??
    d.contact_phone;
  const nifRaw =
    d.nif ??
    d.vat_number ??
    d.vatNumber ??
    (billing as Record<string, unknown> | undefined)?.nif ??
    (billing as Record<string, unknown> | undefined)?.vat_number;

  const first = typeof d.first_name === "string" ? d.first_name : undefined;
  const last = typeof d.last_name === "string" ? d.last_name : undefined;

  return {
    first_name: first,
    last_name: last,
    name: str(
      d.name ??
        (first || last ? [first, last].filter(Boolean).join(" ") : undefined),
    ),
    email: str(d.email),
    phone: str(phoneRaw),
    nif: str(nifRaw),
    billing_address: asAddress(billing),
    shipping_address: asAddress(shipping),
  };
}

export type OrderStatus =
  | "PENDING_OFFER"
  | "PAYMENT_PENDING"
  | "PAID"
  | "FULFILLMENT_INIT"
  | "PAID_PENDING_FULFILLMENT"
  | "COMPLETED"
  | "REFUNDED"
  | "FAILED"
  | "CANCELLED"
  | "OFFER_SENT";

export type FulfillmentStatus =
  | "PENDING"
  | "CREATED"
  | "ACCEPTED"
  | "CONFIRMED"
  | "CANCELLED"
  | "FAILED";

export interface OrderItemFulfillment {
  id: number;
  order_id: string;
  cart_item_id: string;
  compound_id: string;
  sku?: string;
  display_name: string;
  unit_index: number;
  selected_components?: string; // JSON: Record<string,string> — IDs only
  storefront_snapshot?: string; // JSON: StorefrontSnapshot — names + prices captured at checkout
  catalog_snapshot?: string; // JSON: raw catalog response (audit)
  unit_price_cents: number;
  created_at: string;
  updated_at: string;

  // Fulfillment
  provider_type?: string;
  provider_reference?: string;
  provider_external_ref?: string;
  provider_order_ref?: string;
  fulfillment_status: FulfillmentStatus;

  // Legacy
  niposom_offer_id?: string;
  niposom_offer_external_id?: string;
  niposom_order_id?: string;
  offer_status?: FulfillmentStatus;
}

export type OrderSource = "standard" | "builder_quote" | (string & {});

// ── Delivery enrichment (mirrored from OMS AdminOrderResponse) ──────────────
// OMS persists only delivery_method_id / selected_poi_id FKs; the admin
// endpoints enrich orders with the human-readable method + pickup POI the
// customer selected at checkout (fail-soft: absent on legacy/unresolvable).

export interface OmsDeliveryMethodInfo {
  code: string;
  label: string;
}

export interface OmsSelectedPOIInfo {
  code: string;
  label: string;
  line1?: string;
  line2?: string;
  city?: string;
  postal_code?: string;
  country?: string;
}

export interface OmsOrder {
  id: string;
  tenant_id: string;
  user_id?: string;
  cart_id: string;
  checkout_session_id: string;
  payment_attempt_id?: string;
  status: OrderStatus;
  source?: OrderSource;
  // F-2.0 proposal fields
  proposal_status?: import("./Proposal").ProposalStatus;
  proposal_version?: number;
  proposal_sent_at?: string;
  customer_email: string;
  customer_name: string;
  customer_data?: string;
  payment_method?: string;
  subtotal_cents: number;
  shipping_cents: number;
  total_cents: number;
  currency: string;
  idempotency_key: string;
  notes?: string;
  failure_reason?: string;
  callback_subscription_id?: string;
  /**
   * Pre-approved Niposom offer ID at order level.
   * Set for builder-quote checkout orders where the checkout bypassed
   * offer creation and used a pre-existing offer instead.
   * Returned by OMS model.Order.NipososOfferID (json:"niposom_offer_id").
   */
  niposom_offer_id?: string;
  delivery_method?: OmsDeliveryMethodInfo;
  selected_poi?: OmsSelectedPOIInfo;
  items?: OrderItemFulfillment[];
  created_at: string;
  updated_at: string;
  aggregate_status: FulfillmentStatus;
}

export interface OmsOrdersListResponse {
  orders: OmsOrder[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}
